// Client reports — the nightly auto-drafts and what became of each one
// (20261015120000_client_report_review.sql). RLS decides which rows come back
// (a client sees only 'sent'); review_client_report decides who may edit or
// approve a draft: the project's manager (projects.lead) or pipeline:full.
// The same people keep each project's recipients and press Send on an approved
// report (20261015221000_client_report_email.sql); the report-mail Edge
// Function emails it and marks it sent. Nothing in the browser marks it sent.
import { supabase } from './supabase';

export type ReportStatus = 'draft' | 'approved' | 'sending' | 'sent';

// Categorical, not status hues: approved isn't "done" and a draft isn't "late".
export const REPORT_STATUS = {
  draft: { label: 'Draft', tone: 'neutral' },
  approved: { label: 'Approved', tone: 'sky' },
  sending: { label: 'Sending', tone: 'lilac' },
  sent: { label: 'Sent', tone: 'iris' },
} as const satisfies Record<ReportStatus, { label: string; tone: string }>;

export type ClientReport = {
  id: string;
  /** 'YYYY-MM-DD', the org's day. */
  reportDate: string;
  summary: string;
  status: ReportStatus;
  projectId: string;
  projectName: string;
  projectCode: string | null;
  client: string | null;
  /** projects.lead: the manager who may review it. */
  managerId: string | null;
  /** Why the last send failed; the report is Approved again. */
  sendError: string | null;
};

type Row = {
  id: string;
  report_date: string;
  summary: string;
  status: ReportStatus;
  send_error: string | null;
  project_id: string;
  projects: Project | Project[] | null;
};
type Project = { name: string; code: string | null; client: string | null; lead: string | null };

/** Newest day first. `failed` marks a read that errored: not "no reports" (§7). */
export async function listClientReports(): Promise<ClientReport[] & { failed?: true }> {
  if (!supabase) return [];
  const { data, error } = await supabase
    .from('client_reports')
    .select(
      // code:key: a project's shown ID is its key (20261016090000).
      'id, report_date, summary, status, send_error, project_id, projects(name, code:key, client, lead)',
    )
    .order('report_date', { ascending: false });
  if (error) return Object.assign([], { failed: true as const });
  return ((data as Row[]) ?? []).map((r) => {
    // A to-one embed is an object, but the typings can widen it to an array.
    const p = Array.isArray(r.projects) ? r.projects[0] : r.projects;
    return {
      id: r.id,
      reportDate: r.report_date,
      summary: r.summary,
      status: r.status,
      projectId: r.project_id,
      projectName: p?.name ?? 'Project',
      projectCode: p?.code ?? null,
      client: p?.client ?? null,
      managerId: p?.lead ?? null,
      sendError: r.send_error,
    };
  });
}

/** Save a draft's text (`summary`), approve it, or both. The server re-checks who. */
export async function reviewClientReport(
  id: string,
  change: { summary?: string; approve?: boolean },
): Promise<{ error: { message: string } | null }> {
  if (!supabase) return { error: { message: 'Not connected.' } };
  const { error } = await supabase.rpc('review_client_report', {
    p_id: id,
    p_summary: change.summary ?? null,
    p_approve: change.approve ?? false,
  });
  return { error };
}

/** Queue an approved report for the client. The server re-checks who, and that
 *  the project has recipients. */
export async function sendClientReport(id: string): Promise<{ error: { message: string } | null }> {
  if (!supabase) return { error: { message: 'Not connected.' } };
  const { error } = await supabase.rpc('send_client_report', { p_id: id });
  return { error };
}

/** A project's recipients; RLS returns them to its manager and pipeline:full
 *  only. null = the read failed. */
export async function getReportRecipients(projectId: string): Promise<string[] | null> {
  if (!supabase) return [];
  const { data, error } = await supabase
    .from('client_report_recipients')
    .select('emails')
    .eq('project_id', projectId)
    .maybeSingle();
  if (error) return null;
  return (data as { emails: string[] } | null)?.emails ?? [];
}

export async function saveReportRecipients(
  projectId: string,
  emails: string[],
): Promise<{ error: { message: string } | null }> {
  if (!supabase) return { error: { message: 'Not connected.' } };
  const { error } = await supabase
    .from('client_report_recipients')
    .upsert({ project_id: projectId, emails });
  return { error };
}

// The table's check, in the browser: lower-case, one address per entry, ≤ 20.
const EMAIL = /^[^\s@<>",;]+@[^\s@<>",;]+\.[^\s@<>",;.]+$/;
export const MAX_RECIPIENTS = 20;

/** Addresses typed one per line or separated by commas: lower-cased, deduped,
 *  and the ones that aren't addresses. */
export function parseEmails(text: string): { emails: string[]; bad: string[] } {
  const all = [
    ...new Set(
      text
        .split(/[\s,;]+/)
        .map((e) => e.trim().toLowerCase())
        .filter(Boolean),
    ),
  ];
  return { emails: all.filter((e) => EMAIL.test(e)), bad: all.filter((e) => !EMAIL.test(e)) };
}
